import { useCallback } from 'react';

import { createActionsHook, createHook, createStore } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';
import type {
	BrowserContextPayloadData,
	ChatContextPayload,
	ChatContextState,
	DashboardInsightsActionsPayloadData,
	EditorContextPayloadData,
	WhiteboardContextPayloadData,
	WorkflowContextPayloadData,
} from '@atlaskit/rovo-triggers';
import type { ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands';

import { useUserPreferences } from '../../user-preferences';

export type Store = {
	editorContext: EditorContextPayloadData;
	whiteboardContext?: WhiteboardContextPayloadData;
	browserContext?: BrowserContextPayloadData;
	dashboardContext?: DashboardInsightsActionsPayloadData;
	jiraWorkflowContext?: WorkflowContextPayloadData;
	// All the new context will be added under this key
	aiFeatureContext: ChatContextState;
};

type ContentType = 'page' | 'whiteboard';

const store = createStore({
	initialState: {
		editorContext: undefined,
		whiteboardContext: undefined,
		browserContext: undefined,
		dashboardContext: undefined,
		jiraWorkflowContext: undefined,
		aiFeatureContext: {},
	} as Store,
	actions: {
		getAllContext:
			() =>
			({ getState }) =>
				getState(),
		getAIFeatureContext:
			() =>
			({ getState }) => {
				const currentState = getState().aiFeatureContext;
				// Delete all properties that are undefined
				Object.keys(currentState).forEach(
					(key) => currentState[key] === undefined && delete currentState[key],
				);
				// Avoid sending an empty object
				return Object.keys(currentState).length ? currentState : undefined;
			},
		setAIFeatureContext:
			(chatContextData: ChatContextPayload) =>
			({ getState, setState }) => {
				const currentAIFeatureContextState = getState().aiFeatureContext;
				const { contextKey, setContext } = chatContextData;
				if (contextKey) {
					const contextUpdated = setContext(currentAIFeatureContextState[contextKey]);

					setState({
						aiFeatureContext: {
							...currentAIFeatureContextState,
							[contextKey]: contextUpdated,
						},
					});
				}
			},
		getDashboardContext:
			() =>
			({ getState }) =>
				getState().dashboardContext,
		setDashboardContext:
			(dashboardContext: DashboardInsightsActionsPayloadData) =>
			({ setState }) => {
				setState({
					dashboardContext,
				});
			},
		getJiraWorkflowContext:
			() =>
			({ getState }) =>
				getState().jiraWorkflowContext,
		setJiraWorkflowContext:
			(jiraWorkflowContext: WorkflowContextPayloadData) =>
			({ setState }) => {
				setState({
					jiraWorkflowContext,
				});
			},
		getEditorContext:
			() =>
			({ getState }) =>
				getState().editorContext,
		setEditorContext:
			(editorContext: EditorContextPayloadData) =>
			({ setState }) => {
				setState({
					editorContext,
				});
			},
		getWhiteboardContext:
			() =>
			({ getState }) =>
				getState().whiteboardContext,
		setWhiteboardContext:
			(whiteboardContext: WhiteboardContextPayloadData) =>
			({ setState }) => {
				setState({
					whiteboardContext,
				});
			},
		getBrowserContext:
			() =>
			({ getState }) =>
				getState().browserContext,
		setBrowserContext:
			(browserContext: BrowserContextPayloadData) =>
			({ setState }) => {
				setState({
					browserContext,
				});
			},
	},
});

export const useChatContextStoreActions = createActionsHook(store);
export const useChatContextEditorContext = createHook(store, {
	selector: (state) => state.editorContext,
});
export const useChatContextWhiteboardContext = createHook(store, {
	selector: (state) => state.whiteboardContext,
});

// For testing
export { store };

export const useChatContextProvider = ({
	getContentBody,
	stagingAreaEnabled,
}: {
	getContentBody: (type: ContentType) => Promise<ReadContentCommandResult | undefined>;
	stagingAreaEnabled: boolean;
}) => {
	const { preferences } = useUserPreferences();
	const sendEditorContext = preferences.useEditorContext;

	const actions = useChatContextStoreActions();

	const fetchContent = useCallback(
		async (type: ContentType) => {
			const contentBody: ReadContentCommandResult | undefined = await getContentBody(type);

			if (!contentBody || contentBody.contentType !== type) {
				return undefined;
			}

			switch (contentBody.contentType) {
				case 'page': {
					if (!contentBody.pageBody) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						document: {
							type: 'text/adf' as const,
							content: contentBody.pageBody,
						},
						selection: {
							type: 'text/markdown' as const,
							content: '',
						},
						selectionFragment: contentBody.selectionFragment,
						selectionLocalIds: contentBody.selectionLocalIds,
					};
				}
				case 'whiteboard': {
					if (!contentBody.svg) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						type: 'image/svg+xml' as const,
						content: contentBody.svg,
					};
				}
				default:
					return undefined;
			}
		},
		[getContentBody],
	);

	const getEditorContext = useCallback(
		async ({ isFirstPrompt, source }: { isFirstPrompt: boolean; source: string | undefined }) => {
			if (
				(stagingAreaEnabled || source === 'CONFLUENCE_INLINE_EDITOR') &&
				fg('aifc_create_enabled')
			) {
				if (!isFirstPrompt) {
					return (await fetchContent('page')) as EditorContextPayloadData;
				}
				return undefined;
			} else if (sendEditorContext) {
				return actions.getEditorContext();
			}
			return undefined;
		},
		[stagingAreaEnabled, actions, sendEditorContext, fetchContent],
	);

	const getWhiteboardContext = useCallback(
		async ({ isFirstPrompt, source }: { isFirstPrompt: boolean; source: string | undefined }) => {
			if (
				(stagingAreaEnabled || source === 'CONFLUENCE_INLINE_WHITEBOARD') &&
				fg('aifc_create_enabled')
			) {
				if (!isFirstPrompt) {
					return (await fetchContent('whiteboard')) as WhiteboardContextPayloadData;
				}
				return undefined;
			} else if (sendEditorContext) {
				return actions.getWhiteboardContext();
			}
			return undefined;
		},
		[stagingAreaEnabled, actions, sendEditorContext, fetchContent],
	);

	return {
		...actions,
		getEditorContext,
		getWhiteboardContext,
	};
};
